import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import MapaCompletoSection from "@/components/MapaCompletoSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VALID_SOURCES = new Set(["resultado_test", "email_3", "email_5"]);

/**
 * /mapa-completo/:leadId — premium intent capture page.
 *
 * Used as the landing for the "QUIERO MI MAPA COMPLETO" link inside
 * Email #3 and Email #5. Reads ?source=email_3 (or email_5) from the
 * query string, records the click and renders the full premium section.
 */
export default function MapaCompletoPage() {
  const { leadId } = useParams();
  const [searchParams] = useSearchParams();
  const rawSource = (searchParams.get("source") || "resultado_test").toLowerCase();
  const source = VALID_SOURCES.has(rawSource) ? rawSource : "resultado_test";

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    if (!leadId) {
      setError("Lead no encontrado");
      setLoading(false);
      return;
    }

    // Track the click first (best-effort, do not block render)
    axios.post(`${API}/leads/${leadId}/map-click`, { source }).catch(() => {});

    axios
      .get(`${API}/lead/${leadId}`)
      .then(({ data }) => {
        if (mounted) {
          setLead(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (mounted) {
          setError("No pudimos cargar tu mapa");
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [leadId, source]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-white">
        <Loader2 className="w-6 h-6 animate-spin text-[#29BC9B]" />
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-white px-6 text-center">
        <div>
          <h1 className="font-display text-3xl mb-3">Mapa no encontrado</h1>
          <a href="/" className="text-[#29BC9B] underline text-sm">
            Volver al inicio
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="mapa-completo-page">
      <header className="border-b border-white/8 px-6 md:px-12 h-16 flex items-center">
        <a href="/" className="font-display text-sm tracking-tighter text-white/80">
          PLAY YOUR GAME™
        </a>
      </header>

      <main className="pb-24">
        <MapaCompletoSection lead={lead} source={source} autoOpenModal />
      </main>

      <footer className="border-t border-white/5 px-6 md:px-12 py-10 text-center text-[10px] uppercase tracking-[0.3em] text-white/30">
        © PLAY YOUR GAME™ — Evolución bajo presión
      </footer>
    </div>
  );
}
